import snippet from "@segment/snippet";
import * as Sentry from "@sentry/nextjs";
import axios from "axios";
import LogRocket from "logrocket";
import Head from "next/head";
import router, { withRouter } from "next/router";
import Script from "next/script";
import { useEffect, useState } from "react";
import { GlobalStyles } from "../styles/global";

import { PROCESS, SEGMENT_WRITE_KEY } from "../constants";
import { ProvideAuth } from "../services/auth";
import { isClient } from "../styles/utils";
import { SegmentIdentify } from "../utils/segment";

import { HubspotChat } from "@/components/HubspotChat/HubspotChat";
import { PortalProvider } from "@/components/Portal";
import { DEFAULT_REDIRECT, roleConstraints } from "@/constants/roles";
import { useCheckAuth } from "@/hooks/useCheckAuth";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { queryClient } from "@/services/api";
import colours from "@/styles/colours";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine.css";
import "react-dates/initialize";
import "react-dates/lib/css/_datepicker.css";
import { Toaster } from "react-hot-toast";
import "react-loader-spinner/dist/loader/css/react-spinner-loader.css";
import "react-tooltip/dist/react-tooltip.css";
import "../styles/fonts.css";
import "../styles/mobile.css";

axios.defaults.withCredentials = true;

// This code is only for TypeScript
declare global {
  interface Window {
    __TANSTACK_QUERY_CLIENT__: import("@tanstack/query-core").QueryClient;
  }
}

// This code is for all users
if (typeof window !== "undefined") {
  window.__TANSTACK_QUERY_CLIENT__ = queryClient;
}

// Logrocket

if (process.env.NODE_ENV == "production") {
  LogRocket.init("lovelight/capture-swoji");
}

// Segment

const segmentOptions = {
  apiKey: SEGMENT_WRITE_KEY,
  page: false,
};

const handleRouterChange = (url) => {
  isClient() && (window as any)?.analytics?.page?.(url);
};

function Wrapper({ Component, pageProps, router: routerProps }) {
  return (
    <QueryClientProvider client={queryClient}>
      <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />
      <App Component={Component} pageProps={pageProps} router={routerProps} />
    </QueryClientProvider>
  );
}

function App({ Component, pageProps, router: routerProps }) {
  const [authenticated, setAuthenticated] = useState(false);
  const { user: userData } = useCurrentUser();
  const { checkAuth, isError } = useCheckAuth();

  // Reset React Query cache on every deploy
  useEffect(() => {
    if (typeof window !== "undefined") {
      // Check if this is a fresh deploy by comparing build time
      const lastBuildTime = localStorage.getItem("lastBuildTime");
      const currentBuildTime =
        process.env.NEXT_PUBLIC_BUILD_TIME || Date.now().toString();

      if (lastBuildTime !== currentBuildTime) {
        // Clear all React Query cache
        queryClient.clear();
        // Store new build time
        localStorage.setItem("lastBuildTime", currentBuildTime);
        console.log("React Query cache cleared due to new deployment");
      }
    }
  }, []);

  useEffect(() => {
    if (isError) {
      router.push("/login");
    }
  }, [isError]);

  const checkAuthFn = async () => {
    const { pathname } = routerProps;
    const pagesToRedirect = [
      "/",
      "/login",
      "/lost-password",
      "/reset-password",
    ];
    const publicPaths = [
      "/lost-password",
      "/reset-password",
      "/login",
      "/download-quote",
      "/quote/preview/[id]/[version]",
    ];

    if (!checkAuth || checkAuth.isAxiosError || checkAuth !== "Logged in") {
      if (!publicPaths.includes(pathname) && !Component.publicRoute) {
        router.push("/login");
      }
    } else {
      // Check if user is an installer and restrict to /ws paths only
      if (!userData) return;
      userData?.roles?.some((role) => {
        const roleConstraint = roleConstraints[role.name];
        if (roleConstraint) {
          if (
            (Array.isArray(roleConstraint.allowedPages) &&
              !roleConstraint.allowedPages?.some((page) =>
                pathname.startsWith(page),
              )) ||
            (Array.isArray(roleConstraint.restrictedPages) &&
              roleConstraint.restrictedPages?.some((page) =>
                pathname.startsWith(page),
              ))
          ) {
            router.push(roleConstraint.defaultRedirect || DEFAULT_REDIRECT);
            return true;
          }
        }
        return false;
      });

      if (pagesToRedirect.includes(pathname)) {
        router.push(DEFAULT_REDIRECT);
      }

      setAuthenticated(true);
    }
  };

  const renderSegment = () => {
    if (PROCESS !== "development") return snippet.min({ ...segmentOptions });
  };

  // Handle component mount
  useEffect(() => {
    if (!userData || !checkAuth) return;
    router.events.on("routeChangeComplete", handleRouterChange);

    if (!Component.publicRoute) {
      checkAuthFn();
    }

    // Cleanup on unmount
    return () => {
      router.events.off("routeChangeComplete", handleRouterChange);
    };
  }, [userData, checkAuth]);

  return (
    <ProvideAuth>
      <Head>
        <meta charSet={"utf-8"} />
        <meta
          name={"viewport"}
          content={
            "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
          }
        />
      </Head>
      {renderSegment() && (
        <Script
          id="segment-script"
          strategy="beforeInteractive"
          dangerouslySetInnerHTML={{
            __html: renderSegment() || "",
          }}
        />
      )}

      <PortalProvider>
        <GlobalStyles />
        {!Component.publicRoute && (
          <>
            <SegmentIdentify />
          </>
        )}
        {/* <Maintenance /> */}

        {(Component.publicRoute || authenticated) && (
          <>
            <Component {...pageProps} />
            {authenticated && (
              <Sentry.ErrorBoundary>
                <HubspotChat />
              </Sentry.ErrorBoundary>
            )}
          </>
        )}
        <Toaster
          position="bottom-left"
          toastOptions={{
            style: {
              backgroundColor: colours.black,
              color: colours.white,
            },
            icon: <span></span>,
            duration: 2000,
            success: {
              style: {
                backgroundColor: colours.black,
                color: colours.white,
              },
              icon: <span></span>,
              duration: 2000,
            },
            error: {
              style: {
                backgroundColor: colours.black,
                color: colours.white,
              },
              icon: <span></span>,
              duration: 2000,
            },
          }}
        />
      </PortalProvider>
    </ProvideAuth>
  );
}

export default withRouter(Wrapper);
